import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useColorPalette } from '../hooks/useColorPalette';
import { getNodeApiUrl } from '../config/api.config';
import EmployeeSelect from '../components/EmployeeSelect';
import VehicleSelect from '../components/VehicleSelect';
import AttachmentsViewer from '../components/AttachmentsViewer';
import DynamicIcon from '../components/DynamicIcon';

// Componente per preview file (identico a CreateJob)
const FilePreview = ({ file, onDelete, palette }) => {
    const [previewError, setPreviewError] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const fileName = file.fileName || file.name || file.originalname || 'File allegato';
    const fileExtension = fileName.split('.').pop().toLowerCase();
    const mimeType = file.fileType || file.type || '';
    // Supporta sia file già caricati (url/fileUrl) che nuovi file (preview)
    const fileUrl = file.fileUrl || file.url || file.preview || '';
    
    // LOG DEBUG
    console.log('📸 FilePreview render:', {
        fileName,
        fileExtension,
        mimeType,
        fileUrl: fileUrl ? fileUrl.substring(0, 50) + '...' : 'NO URL',
        hasFile: !!file.file,
        isPreview: !!file.preview
    });
    
    // Determina se è un'immagine basandosi sull'estensione o sul MIME type
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileExtension) || 
                     mimeType.startsWith('image/') ||
                     fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i);
    
    // Determina l'icona appropriata per i documenti non-immagine
    let fileIcon = 'fa-file';
    if (['pdf'].includes(fileExtension) || mimeType.includes('pdf')) fileIcon = 'fa-file-pdf';
    else if (['doc', 'docx'].includes(fileExtension) || mimeType.includes('word')) fileIcon = 'fa-file-word';
    else if (['xls', 'xlsx'].includes(fileExtension) || mimeType.includes('excel') || mimeType.includes('spreadsheet')) fileIcon = 'fa-file-excel';
    else if (['ppt', 'pptx'].includes(fileExtension) || mimeType.includes('powerpoint') || mimeType.includes('presentation')) fileIcon = 'fa-file-powerpoint';
    else if (['zip', 'rar', 'tar', 'gz', '7z'].includes(fileExtension) || mimeType.includes('zip') || mimeType.includes('archive')) fileIcon = 'fa-file-archive';
    else if (['txt', 'rtf'].includes(fileExtension) || mimeType.includes('text')) fileIcon = 'fa-file-alt';
    
    // Gestione dell'apertura del file
    const handleOpen = () => {
        if (fileUrl) {
            console.log(`Apertura file: ${fileUrl}`);
            window.open(fileUrl, '_blank');
        }
    };
    
    return (
        <div className="flex flex-col border rounded-lg overflow-hidden bg-white" style={{ 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
        }}>
            {/* Anteprima file */}
            <div 
                className="relative h-32 flex items-center justify-center cursor-pointer"
                style={{
                    backgroundColor: isImage ? '#f3f4f6' : (palette.neutralBg || '#f3f4f6') 
                }}
                onClick={handleOpen}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Badge per file esistente */}
                {file.isExistingFile && (
                    <div className="absolute bottom-1 left-1 z-10">
                        <span className="text-xs rounded-md px-1.5 py-0.5" style={{ 
                            backgroundColor: palette.successBg || '#d1fae5',
                            color: palette.success || '#059669'
                        }}>
                            <i className="fa-solid fa-check-circle mr-1"></i>
                            Salvato
                        </span>
                    </div>
                )}
                
                {isImage && fileUrl && !previewError ? (
                    // Mostra anteprima per le immagini
                    <>
                        {console.log('🖼️ Rendering immagine:', fileName, 'URL:', fileUrl)}
                        <img 
                            src={fileUrl}
                            alt={fileName}
                            className="h-full w-full object-contain p-1"
                            style={{ maxHeight: '128px' }}
                            onLoad={() => console.log('✅ Immagine caricata:', fileName)}
                            onError={(e) => {
                                console.error('❌ Errore caricamento:', fileName, 'URL:', fileUrl);
                                // Prova con URL decodificato
                                const decodedUrl = decodeURIComponent(fileUrl);
                                if (decodedUrl !== fileUrl && !e.target.dataset.retried) {
                                    console.log('🔄 Retry con URL decodificato');
                                    e.target.src = decodedUrl;
                                    e.target.dataset.retried = 'true';
                                } else {
                                    console.error('❌ Impossibile caricare immagine');
                                    setPreviewError(true);
                                }
                            }}
                        />
                    </>
                ) : (
                    // Mostra icona per documenti o immagini con errore
                    <div className="flex flex-col items-center justify-center">
                        <i className={`fa-solid ${fileIcon} text-4xl mb-2`} style={{ 
                            color: palette.primary || '#3182ce' 
                        }}></i>
                        {previewError && (
                            <span className="text-xs text-gray-500">Anteprima non disponibile</span>
                        )}
                    </div>
                )}
                
                {/* Overlay hover con icona per aprire - solo per immagini */}
                {fileUrl && isImage && isHovered && (
                    <div 
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            pointerEvents: 'none'
                        }}
                    >
                        <i className="fa-solid fa-eye" style={{ color: 'white', fontSize: '1.5rem' }}></i>
                    </div>
                )}
            </div>
            
            {/* Footer con nome file e pulsante elimina */}
            <div className="p-2 border-t flex justify-between items-center gap-2" style={{backgroundColor: palette.neutralBg || '#f9fafb'}}>
                {/* Nome file e tipo */}
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" title={fileName}>
                        {fileName}
                    </p>
                    {mimeType && (
                        <span className="text-xs" style={{ color: palette.neutralText || '#9ca3af' }}>
                            {mimeType.split('/')[1] || mimeType}
                        </span>
                    )}
                </div>
                {/* Icona elimina - SEMPRE VISIBILE */}
                <button
                    type="button"
                    className="flex-shrink-0 hover:scale-110 transition-transform"
                    style={{
                        color: palette.danger || '#e53e3e',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        minWidth: '32px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onDelete) onDelete();
                    }}
                    title="Elimina file"
                >
                    <i className="fa-solid fa-trash-alt" style={{ fontSize: '1rem' }}></i>
                </button>
            </div>
        </div>
    );
};

const LoadEmployeeHours = () => {
    const colors = useColorPalette();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const appointmentId = searchParams.get('appointmentId');
    const token = useSelector((state) => state.auth.token);
    const accountData = useSelector((state) => state.account.data);
    
    // Stati
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedDetail, setExpandedDetail] = useState(null);
    const [editingDetail, setEditingDetail] = useState(null);
    const [editingDetailIndex, setEditingDetailIndex] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [detailToDelete, setDetailToDelete] = useState(null);
    
    // Form data
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [formData, setFormData] = useState({
        workHours: 0,
        workMinutes: 0,
        isClosed: false,
        notes: '',
        files: []
    });
    
    // Carica dettagli appuntamento
    useEffect(() => {
        if (appointmentId && accountData) {
            fetchAppointmentDetails();
        }
    }, [appointmentId, accountData]);
    
    const fetchAppointmentDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(getNodeApiUrl('appointments/detail'), {
                headers: { 'auth-token': token },
                params: { 
                    appointmentId,
                    accountId: accountData._id
                }
            });
            // Blocca l'accesso per appuntamenti generici
            if (response.data.isGenericAppointment || !response.data.jobId) {
                toast.error('Non è possibile caricare ore per gli appuntamenti generici');
                navigate(-1);
                return;
            }
            setAppointment(response.data);
        } catch (error) {
            console.error('Errore nel caricamento appuntamento:', error);
            toast.error('Errore nel caricamento dei dettagli dell\'appuntamento');
        } finally {
            setLoading(false);
        }
    };
    
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const newFiles = files.map(file => ({
            file,
            name: file.name,
            type: file.type,
            size: file.size,
            preview: URL.createObjectURL(file)
        }));
        setFormData(prev => ({
            ...prev,
            files: [...prev.files, ...newFiles]
        }));
    };
    
    const removeFile = (index) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));
    };
    
    const handleEditDetail = (detail, index) => {
        setEditingDetail(detail);
        setEditingDetailIndex(index);
        
        // Imposta dipendente selezionato
        if (detail.userId && detail.userLabel) {
            setSelectedEmployee({
                value: detail.userId,
                label: detail.userLabel,
                id: detail.userId
            });
        }
        
        // Imposta veicolo selezionato
        if (detail.vehicleId && detail.vehicleLabel) {
            setSelectedVehicle({
                value: detail.vehicleId,
                label: detail.vehicleLabel,
                id: detail.vehicleId
            });
        }
        
        // Normalizza i file esistenti per la visualizzazione
        const existingFiles = (detail.files || []).map(file => {
            if (typeof file === 'string') {
                // Se è una stringa, assumiamo sia un URL
                return {
                    url: file,
                    fileUrl: file,
                    fileName: file.split('/').pop(),
                    name: file.split('/').pop(),
                    isExistingFile: true // Marca come file già salvato
                };
            } else {
                // Se è già un oggetto, assicurati che abbia i campi necessari
                return {
                    url: file.url || file.fileUrl || '',
                    fileUrl: file.url || file.fileUrl || '',
                    fileName: file.fileName || file.name || 'File',
                    name: file.fileName || file.name || 'File',
                    fileType: file.fileType || file.type || '',
                    isExistingFile: true // Marca come file già salvato
                };
            }
        });
        
        setFormData({
            workHours: detail.workHours || 0,
            workMinutes: detail.workMinutes || 0,
            isClosed: detail.isClosed || false,
            notes: detail.notes || '',
            files: existingFiles
        });
    };
    
    const handleCancelEdit = () => {
        setEditingDetail(null);
        setEditingDetailIndex(null);
        setSelectedEmployee(null);
        setSelectedVehicle(null);
        setFormData({
            workHours: 0,
            workMinutes: 0,
            isClosed: false,
            notes: '',
            files: []
        });
    };
    
    const handleDeleteDetail = (detail, index) => {
        setDetailToDelete({ detail, index });
        setShowDeleteModal(true);
    };
    
    const confirmDeleteDetail = async () => {
        if (!detailToDelete) return;
        
        setSaving(true);
        try {
            await axios.post(
                getNodeApiUrl('appointments/deleteClosingDetail'),
                {
                    appointmentId,
                    accountId: accountData._id,
                    detailIndex: detailToDelete.index
                },
                {
                    headers: { 'auth-token': token }
                }
            );
            
            toast.success('Attività eliminata con successo!');
            setShowDeleteModal(false);
            setDetailToDelete(null);
            fetchAppointmentDetails();
        } catch (error) {
            console.error('Errore nell\'eliminazione:', error);
            toast.error('Errore nell\'eliminare l\'attività');
        } finally {
            setSaving(false);
        }
    };
    
    const handleEmployeeChange = (selected) => {
        // Controlla se il dipendente selezionato ha già un'attività registrata
        if (selected && appointment.closingDetails && appointment.closingDetails.length > 0) {
            const employeeId = selected.value || selected.id;
            
            // Cerca se esiste già un'attività per questo dipendente
            const existingDetailIndex = appointment.closingDetails.findIndex(detail => detail.userId === employeeId);
            
            if (existingDetailIndex !== -1) {
                // Se stiamo modificando questa stessa attività, permettiamo di mantenere il dipendente
                if (editingDetailIndex !== null && existingDetailIndex === editingDetailIndex) {
                    setSelectedEmployee(selected);
                    return;
                }
                
                // Altrimenti blocchiamo la selezione
                toast.warning(`Il dipendente ${selected.label} ha già un'attività registrata per questo appuntamento. Puoi modificarla dalla lista a sinistra.`);
                setSelectedEmployee(null);
                return;
            }
        }
        
        // Se il dipendente non ha attività registrate, o è stato deselezionato, procediamo
        setSelectedEmployee(selected);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedEmployee) {
            toast.warning('Seleziona un dipendente');
            return;
        }
        
        setSaving(true);
        
        try {
            // Upload files su Google Cloud Storage
            const uploadedFiles = [];
            
            console.log('📁 Files da processare:', formData.files.length);
            
            for (let i = 0; i < formData.files.length; i++) {
                const fileData = formData.files[i];
                
                // Se il file ha già un URL (file esistente), mantienilo
                if (fileData.url || fileData.fileUrl) {
                    uploadedFiles.push({
                        url: fileData.url || fileData.fileUrl,
                        fileName: fileData.fileName || fileData.name,
                        fileType: fileData.fileType || fileData.type
                    });
                    console.log(`✅ File ${i+1}: Già salvato`);
                    continue;
                }
                
                // Nuovo file da caricare
                if (fileData.file) {
                    console.log(`⬆️ File ${i+1}: Caricamento su GCS...`);
                    const formDataUpload = new FormData();
                    formDataUpload.append('accountId', accountData._id);
                    formDataUpload.append('file', fileData.file);
                    formDataUpload.append('path', 'appuntamenti');
                    formDataUpload.append('uploadTitle', `Appuntamento ${appointment.title || appointmentId} - ${fileData.name}`);
                    
                    try {
                        const uploadResponse = await axios.post(
                            `${import.meta.env.VITE_API_BASE_URL || ''}/files/upload`,
                            formDataUpload,
                            {
                                headers: { 
                                    'auth-token': token,
                                    'Content-Type': 'multipart/form-data'
                                }
                            }
                        );
                        
                        // Estrai l'URL dalla risposta (supporta fileUrl, url, urlFile)
                        const uploadedUrl = uploadResponse.data.fileUrl || uploadResponse.data.url || uploadResponse.data.urlFile;
                        
                        if (uploadedUrl) {
                            uploadedFiles.push({
                                url: uploadedUrl,
                                fileName: uploadResponse.data.fileName || fileData.name,
                                fileType: uploadResponse.data.kind || fileData.type
                            });
                            console.log(`✅ File ${i+1}: Caricato su GCS -`, uploadedUrl);
                        } else {
                            console.warn(`⚠️ File ${i+1}: Risposta upload non contiene URL`, uploadResponse.data);
                        }
                    } catch (uploadError) {
                        console.error(`❌ Errore upload file ${i+1}:`, uploadError);
                        toast.error(`Errore caricamento file: ${fileData.name}`);
                    }
                }
            }
            
            console.log('📦 Totale file caricati:', uploadedFiles.length);
            
            // Crea/Aggiorna closing detail
            console.log('📝 selectedEmployee:', selectedEmployee);
            console.log('📝 selectedEmployee.value:', selectedEmployee?.value);
            console.log('📝 selectedEmployee.id:', selectedEmployee?.id);
            
            const closingDetail = {
                userId: selectedEmployee.value || selectedEmployee.id,
                userLabel: selectedEmployee.label,
                vehicleId: selectedVehicle ? (selectedVehicle.value || selectedVehicle.id) : null,
                vehicleLabel: selectedVehicle ? selectedVehicle.label : '',
                workHours: parseInt(formData.workHours) || 0,
                workMinutes: parseInt(formData.workMinutes) || 0,
                isClosed: formData.isClosed,
                notes: formData.notes,
                // Solo gli indirizzi: l'app mobile si aspetta un array di stringhe e con
                // oggetti scarta l'appuntamento senza mostrarlo (#017)
                files: uploadedFiles.map(f => f.url).filter(Boolean),
                timestamp: new Date()
            };
            
            console.log('💾 Salvataggio attività con', uploadedFiles.length, 'file(s)');
            console.log('💾 closingDetail da inviare:', closingDetail);
            console.log('💾 closingDetail.userId:', closingDetail.userId);
            
            await axios.post(
                getNodeApiUrl('appointments/updateClosingDetails'),
                {
                    appointmentId,
                    accountId: accountData._id,
                    closingDetail,
                    detailIndex: editingDetailIndex !== null ? editingDetailIndex : undefined
                },
                {
                    headers: { 'auth-token': token }
                }
            );
            
            toast.success(editingDetail ? 'Attività modificata con successo!' : 'Attività aggiunta con successo!');
            handleCancelEdit();
            fetchAppointmentDetails();
        } catch (error) {
            console.error('Errore nel salvare:', error);
            // Mostra il messaggio di errore dal backend se disponibile
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Errore nel salvare l\'attività';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };
    
    if (loading) {
        return <div className="container-fluid py-4">Caricamento...</div>;
    }
    
    if (!appointment) {
        return <div className="container-fluid py-4">Appuntamento non trovato</div>;
    }

    return (
        <div className="container-fluid py-4">
            <ToastContainer position="top-right" autoClose={3000} />
            
            {/* Modale di conferma eliminazione */}
            {showDeleteModal && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => !saving && setShowDeleteModal(false)}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '2rem',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-center mb-4">
                            <div 
                                style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fee2e2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1rem'
                                }}
                            >
                                <i className="fa-solid fa-exclamation-triangle text-danger" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <h3 className="fw-bold mb-2">Conferma Eliminazione</h3>
                            <p className="text-muted mb-0">
                                Sei sicuro di voler eliminare l'attività di <strong>{detailToDelete?.detail.userLabel}</strong>?
                            </p>
                            <p className="text-muted small mt-2">
                                Questa azione non può essere annullata.
                            </p>
                        </div>
                        
                        <div className="d-flex gap-3 justify-content-center">
                            <button 
                                className="btn btn-outline-secondary px-4"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={saving}
                            >
                                <i className="fa-solid fa-times me-2"></i>
                                Annulla
                            </button>
                            <button 
                                className="btn btn-danger px-4"
                                onClick={confirmDeleteDetail}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Eliminazione...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-trash me-2"></i>
                                        Elimina
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <h1 
                            className="mb-0 fw-bold" 
                            style={{ 
                                color: colors.primary || '#007bff',
                                fontSize: '2rem'
                            }}
                        >
                            Carica Ore - {appointment.title || 'Appuntamento'}
                        </h1>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/jobsCalendar', { 
                                state: { 
                                    openJobDetail: true,
                                    jobId: appointment?.jobId,
                                    appointmentId: appointmentId
                                }
                            })}
                        >
                            <i className="fa-solid fa-arrow-left me-2"></i>
                            Indietro
                        </button>
                    </div>
                </div>
            </div>
            
            <div className="row">
                {/* Colonna sinistra - Lista closing details */}
                <div className="col-12 col-md-5">
                    <div className="card">
                        <div className="card-header" style={{ backgroundColor: colors.primary, color: 'white' }}>
                            <h5 className="mb-0">Attività Registrate</h5>
                        </div>
                        <div className="card-body">
                            {appointment.closingDetails && appointment.closingDetails.length > 0 ? (
                                appointment.closingDetails.map((detail, idx) => (
                                    <div key={idx} className="border rounded p-3 mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0">{detail.userLabel}</h6>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="fw-bold">
                                                    {String(detail.workHours || 0).padStart(2, '0')}:{String(detail.workMinutes || 0).padStart(2, '0')}
                                                </span>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleEditDetail(detail, idx)}
                                                    title="Modifica attività"
                                                >
                                                    <i className="fa-solid fa-edit"></i>
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteDetail(detail, idx)}
                                                    title="Elimina attività"
                                                >
                                                    <i className="fa-solid fa-trash"></i>
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setExpandedDetail(expandedDetail === idx ? null : idx)}
                                                    title={expandedDetail === idx ? "Nascondi dettagli" : "Mostra dettagli"}
                                                >
                                                    <i className={`fa-solid fa-chevron-${expandedDetail === idx ? 'up' : 'down'}`}></i>
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {expandedDetail === idx && (
                                            <div className="mt-3 pt-3 border-top">
                                                <p><strong>Veicolo:</strong> {detail.vehicleLabel || 'Non specificato'}</p>
                                                <p><strong>Ore:</strong> {detail.workHours || 0}h {detail.workMinutes || 0}m</p>
                                                <p><strong>Stato:</strong> {detail.isClosed ? 'Chiusa' : 'Aperta'}</p>
                                                {detail.notes && <p><strong>Note:</strong> {detail.notes}</p>}
                                                {detail.files && detail.files.length > 0 && (
                                                    <div>
                                                        <strong>Allegati:</strong>
                                                        <AttachmentsViewer 
                                                            attachments={detail.files}
                                                            title="File allegati"
                                                            showPreview={true}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted">Nessuna attività registrata</p>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Colonna destra - Form */}
                <div className="col-12 col-md-7">
                    <div className="card">
                        <div className="card-header" style={{ backgroundColor: colors.primary, color: 'white' }}>
                            <h5 className="mb-0">{editingDetail ? 'Modifica Attività' : 'Aggiungi Attività'}</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Collaboratore *</label>
                                        <EmployeeSelect
                                            selectedOptions={selectedEmployee}
                                            setSelectedOptions={handleEmployeeChange}
                                            isMulti={false}
                                            placeholder="Seleziona collaboratore..."
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Veicolo</label>
                                        <VehicleSelect
                                            selectedOptions={selectedVehicle}
                                            setSelectedOptions={setSelectedVehicle}
                                            isMulti={false}
                                            placeholder="Seleziona veicolo..."
                                        />
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-end gap-3 mb-3">
                                    <div>
                                        <label className="form-label">Ore</label>
                                        <input 
                                            type="number"
                                            className="form-control"
                                            style={{ minWidth: '100px', width: '100px' }}
                                            min="0"
                                            value={formData.workHours}
                                            onChange={(e) => setFormData(prev => ({ ...prev, workHours: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Minuti</label>
                                        <input 
                                            type="number"
                                            className="form-control"
                                            style={{ minWidth: '100px', width: '100px' }}
                                            min="0"
                                            max="59"
                                            value={formData.workMinutes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, workMinutes: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Stato</label>
                                        <div className="form-check form-switch" style={{ paddingTop: '6px' }}>
                                            <input 
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.isClosed}
                                                onChange={(e) => setFormData(prev => ({ ...prev, isClosed: e.target.checked }))}
                                            />
                                            <label className="form-check-label">
                                                {formData.isClosed ? 'Chiusa' : 'Aperta'}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Note</label>
                                    <textarea 
                                        className="form-control"
                                        rows="3"
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                    />
                                </div>
                                
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Files allegati
                                    </label>
                                    <div className="border rounded-lg p-4 bg-gray-50">
                                        <input
                                            type="file"
                                            multiple
                                            className="w-full mb-3"
                                            onChange={handleFileChange}
                                        />
                                        
                                        {/* Gallery di anteprima file */}
                                        {formData.files.length > 0 && (
                                            <div className="mt-3">
                                                <p className="font-medium mb-3 text-sm" style={{ color: colors.primary }}>
                                                    <i className="fa-solid fa-paperclip me-1"></i>
                                                    Files Allegati ({formData.files.length})
                                                </p>
                                                
                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                                    {formData.files.map((file, index) => (
                                                        <FilePreview
                                                            key={index}
                                                            file={file}
                                                            palette={colors}
                                                            onDelete={() => removeFile(index)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Salvataggio...
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fa-solid fa-${editingDetail ? 'save' : 'plus'} me-2`}></i>
                                                {editingDetail ? 'Modifica Attività' : 'Aggiungi Attività'}
                                            </>
                                        )}
                                    </button>
                                    {editingDetail && (
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-secondary"
                                            onClick={handleCancelEdit}
                                            disabled={saving}
                                        >
                                            <i className="fa-solid fa-times me-2"></i>
                                            Annulla
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoadEmployeeHours;
